import { SORT_DIRECTION } from '@avanta/shared/constants/common';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useServerPaginatedTable } from '@avanta/shared/hooks/useServerPaginatedTable';
import { isEnterpriseAppointmentStatus } from '@avanta/shared/utils/appointments';
import { isLoadingNewData } from '@avanta/shared/utils/table';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
  type Table,
  type Updater,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useFetchEnterpriseAppointments } from 'hooks/useFetchEnterpriseAppointments';
import { useFetchEnterpriseAppointmentsCount } from 'hooks/useFetchEnterpriseAppointmentsCount';
import {
  type AppointmentWithDetails,
  type EnterpriseAppointmentFilter,
  type EnterpriseAppointmentStatus,
  type GetEnterpriseAppointmentsParams,
} from 'types/appointments';
import { matchesAppointmentFilter } from 'utils/appointments';
import { getServerSort } from 'utils/table';

const COLUMN_SORT_BY: Record<string, GetEnterpriseAppointmentsParams['sortBy']> = {
  dateTime: 'dateTime',
  patientName: 'patientName',
  patientId: 'patientId',
};

const getSpecialityFilter = (
  columnFilters: ColumnFiltersState
): GetEnterpriseAppointmentsParams['specialitiesBookable'] => {
  const specialityValue = columnFilters.find(filter => filter.id === 'specialitiesBookable')?.value;
  return typeof specialityValue === 'number' && Number.isInteger(specialityValue)
    ? specialityValue
    : undefined;
};

const getStatusColumnFilter = (
  columnFilters: ColumnFiltersState
): EnterpriseAppointmentStatus | undefined => {
  const statusValue = columnFilters.find(filter => filter.id === 'status')?.value;
  return typeof statusValue === 'string' && isEnterpriseAppointmentStatus(statusValue)
    ? statusValue
    : undefined;
};

export const useFetchAppointments = (
  columns: ColumnDef<AppointmentWithDetails>[]
): {
  table: Table<AppointmentWithDetails>;
  selectedFilter: EnterpriseAppointmentFilter | undefined;
  setFilter: (value: EnterpriseAppointmentFilter | undefined) => void;
} => {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 8 });
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<EnterpriseAppointmentFilter>();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const debouncedSearch = useDebounce(globalFilter, 500).trim();
  const search = debouncedSearch || undefined;
  const { sortBy, sortOrder } = getServerSort(sorting, COLUMN_SORT_BY);
  const specialitiesBookable = getSpecialityFilter(columnFilters);
  const columnStatus = getStatusColumnFilter(columnFilters);
  const hasConflictingFilters = Boolean(
    selectedFilter &&
    columnStatus &&
    !matchesAppointmentFilter({ status: columnStatus }, selectedFilter)
  );
  const status = columnStatus ?? selectedFilter;
  const query = useFetchEnterpriseAppointments(
    {
      offset: pagination.pageIndex,
      limit: pagination.pageSize,
      status,
      search,
      specialitiesBookable,
      sortBy,
      sortOrder: sortOrder ?? SORT_DIRECTION.desc,
    },
    !hasConflictingFilters
  );
  const countQuery = useFetchEnterpriseAppointmentsCount(
    { status, search, specialitiesBookable },
    !hasConflictingFilters
  );

  const resetPage = (): void => {
    setPagination(previous =>
      previous.pageIndex === 0 ? previous : { ...previous, pageIndex: 0 }
    );
  };

  const setFilter = (value: EnterpriseAppointmentFilter | undefined): void => {
    setSelectedFilter(value);
    resetPage();
  };

  const handleGlobalFilterChange = (updater: Updater<string>): void => {
    setGlobalFilter(updater);
    resetPage();
  };

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = updater => {
    setColumnFilters(updater);
    resetPage();
  };

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    setSorting(updater);
    resetPage();
  };

  const table = useServerPaginatedTable({
    columns,
    data: hasConflictingFilters ? [] : (query.data ?? []),
    totalCount: hasConflictingFilters ? 0 : (countQuery.data ?? 0),
    pagination,
    onPaginationChange: setPagination,
    globalFilter,
    onGlobalFilterChange: handleGlobalFilterChange,
    columnFilters,
    onColumnFiltersChange: handleColumnFiltersChange,
    sorting,
    onSortingChange: handleSortingChange,
    manualSorting: true,
    isLoadingData:
      !hasConflictingFilters && (isLoadingNewData(query) || isLoadingNewData(countQuery)),
    pageCountKey: { status, search, specialitiesBookable },
  });

  return {
    table,
    selectedFilter,
    setFilter,
  };
};
